import moment from "moment";
import queryString from "query-string";
import get from "lodash/fp/get";
import compose from "lodash/fp/compose";
import isEqual from "lodash/fp/isEqual";
import toLower from "lodash/fp/toLower";
import pick from "lodash/fp/pick";

import { KIOSK, EVPARKING_UNITGROUP_CODE } from "utils/constants";

// PMS service pricing unit types
export const PRICING_UNIT = {
  PERSON: "Person",
  ROOM: "Room",
  STAY: "Stay",
};

// CMS service guest types
export const GUEST_TYPE = {
  ADULTS: "Adults",
  CHILDREN: "Children",
};

export const findRoomDetailsOfReservation = (reservation, roomDetails) => {
  return roomDetails.find((room) => {
    return room.pms_id === reservation.unitGroup.code;
  });
};

export const childrenAgesArray = (numberOfChildren) => {
  return Array(parseInt(numberOfChildren)).fill(0);
};

export const childrenAgesStr = (numberOfChildren) => {
  return `${childrenAgesArray(numberOfChildren).toString()}`;
};

export const guestCount = (availCheckParams) => {
  return parseInt(availCheckParams.adults) + childrenCount(availCheckParams);
};

export const childrenCount = (availCheckParams) => {
  let childrenCount = 0;
  if (availCheckParams.childrenAges) {
    childrenCount = availCheckParams.childrenAges.split(",").length;
  }
  return childrenCount;
};

export const guestCountForReservation = (reservation) => {
  // Parking reservations have 0 guests
  if (reservation?.unitGroup?.code === EVPARKING_UNITGROUP_CODE) {
    return 0;
  } else {
    const adults = reservation?.adults || 0;
    const children = (reservation?.childrenAges || []).length;

    return adults + children;
  }
};

export const guestCountForReservations = (reservations) => {
  return reservations.reduce(
    (acc, res) => acc + guestCountForReservation(res),
    0
  );
};

const chooseRoomParamsOrder = [
  "propertyId",
  "arrival",
  "departure",
  "adults",
  "childrenAges",
  "promoCode",
  "corporateCode",
];

export const getChooseRoomQueryString = (
  availabilityParamsObj,
  roomsOccupancyObj,
  nextRoomCount
) =>
  queryString.stringify(
    {
      propertyId: availabilityParamsObj.propertyId,
      arrival: availabilityParamsObj.arrival,
      departure: availabilityParamsObj.departure,
      adults: roomsOccupancyObj[nextRoomCount].adults,
      childrenAges: childrenAgesStr(roomsOccupancyObj[nextRoomCount].children),
      promoCode: availabilityParamsObj.promoCode,
      corporateCode: availabilityParamsObj.corporateCode,
    },
    {
      sort: (a, b) =>
        chooseRoomParamsOrder.indexOf(a) - chooseRoomParamsOrder.indexOf(b),
    }
  );

export const nightCount = (availCheckParams) => {
  const arrival = moment(availCheckParams.arrival);
  const departure = moment(availCheckParams.departure);

  return departure.diff(arrival, "days");
};

export const serializeFirebaseUser = (userData) => {
  if (userData.displayName) {
    return {
      id: userData.userId,
      displayName: userData.displayName,
      email: userData.email,
      emailVerified: userData.emailVerified,
      phoneNumber: userData.phoneNumber,
    };
  } else {
    return {
      id: userData.user_id,
      displayName: userData.name,
      email: userData.email,
      emailVerified: userData.email_verified,
      phoneNumber: null,
    };
  }
};

const splitName = (fullName) => {
  // Split by | if present, otherwise by space
  return fullName.includes("|") ? fullName.split("|") : fullName.split(" ");
};

export const generateFirstName = (fullName) => {
  return splitName(fullName).slice(0, -1).join(" ").trim();
};

export const generateLastName = (fullName) => {
  return splitName(fullName).slice(-1)[0].trim();
};

export const formatCurrency = (currencyStr, value, decimals = true) => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currencyStr,
    minimumFractionDigits: decimals ? 2 : 0,
    maximumFractionDigits: decimals ? 2 : 0,
  }).format(value);
};

export const formatQuantity = (quantity, item) => {
  return `${quantity} ${quantity > 1 ? item + "s" : item}`;
};

export const initialsForName = (name) => {
  return name
    .match(/(^\S\S?|\b\S)?/g)
    .join("")
    .match(/(^\S|\S$)?/g)
    .join("")
    .toUpperCase();
};

export const isKioskFromSearchParams = compose(
  isEqual(KIOSK),
  toLower,
  get("source")
);

export const isSingleRoomModeFromSearchParams = () => true; // AJD: Only allowing single room bookings
// params.paymentTerminalId != null;

export const getCommonSearchParams = pick([
  "arrival",
  "corporateCode",
  "departure",
  "promoCode",
  "propertyId",
  "source",
  "paymentTerminalId",
]);

export const sleep = (milliseconds) => {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
};
